import type { MetroAnalyticsReporter } from '../../interaction/metroAnalyticsReporter'
import { HEALTH_ID, reportHealthError } from '../../analytics/appHealthEvents'
import { reportRouteHistoryRecorded } from '../../analytics/routeLibraryEvents'
import { loadHistory, saveHistory } from '../routeLibraryPersistence'
import { canonicalId, makeRouteKey } from './routeKey'
import { recordEntry } from './routeHistoryList'

export interface RecordRouteInput {
  readonly fromSlug: string | null
  readonly toSlug: string | null
  readonly durationMinutes: number
  readonly changesCount: number
  readonly lineIds: readonly string[]
  /** Injected so tests do not depend on the wall clock. */
  readonly nowIso: string
}

/**
 * The single write path into the web route library (spec §4).
 *
 * History is recorded at exactly one moment: the rider opened RouteDetails.
 * An earlier trigger (every slot pick, every query) fills the history with
 * mistypes, which then poison every surface the data exists to feed.
 *
 * Returns `false` when the pair could not form a key — nothing is written
 * and no PRODUCT event is emitted, so the funnel never counts a row that
 * does not exist. That drop is reported as app-health instead: it is
 * otherwise completely invisible (nothing retries, and there is no in-app
 * symptom), and a rising rate means schema drift rather than user
 * behaviour. Best-effort throughout: a storage failure degrades to a health
 * warning inside the persistence layer and must never surface to the rider.
 */
export function recordRouteOpen(
  input: RecordRouteInput,
  reporter: MetroAnalyticsReporter,
): boolean {
  const key = makeRouteKey(input.fromSlug, input.toSlug)
  if (key === null) {
    // Mirrors iOS, whose failable `RouteKey.init` covers BOTH a missing slug
    // and a same-station pair, and which emits this one code for either.
    // Keeping the same trigger set is what makes the platforms' rates
    // comparable — narrowing it on web alone would look like a web-only
    // improvement in a dashboard that is actually a measurement change.
    reportHealthError(
      HEALTH_ID.routeKeySlugMissing,
      `route history skipped: unusable station pair (from=${input.fromSlug ?? ''}, to=${input.toSlug ?? ''})`,
      { domain: 'persistence', severity: 'warning', fallback: 'skip' },
    )
    return false
  }

  const next = recordEntry(loadHistory(), {
    key,
    snapshot: {
      durationMinutes: input.durationMinutes,
      changesCount: input.changesCount,
      lineIds: [...input.lineIds],
      // WRITE time, per contract §2.3 — web has no routing-computation
      // timestamp to use, and inventing one would give the field a
      // different meaning here than on iOS and Android.
      computedAtIso: input.nowIso,
    },
    lastUsedIso: input.nowIso,
    // Recomputed by `recordEntry` from the stored row; this value is ignored.
    useCount: 1,
  })
  saveHistory(next)

  reportRouteHistoryRecorded(reporter, {
    routeKey: canonicalId(key),
    changesCount: input.changesCount,
    durationMinutes: input.durationMinutes,
  })
  return true
}
